import React, { useState, useEffect, useRef, CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string-for-all';
import { getSortedQuery } from '@prisjakt/product-collection-urls';
import { ProductCollectionSortDirection } from '../../../../../types';
import * as S from './styles';
import { IconDropdownDown, IconDropdownUp } from '@prisjakt/ui/icon';
import { remCalc } from '@prisjakt/ui-base';

const SORT_ICON_SIZE = 24;
const SORT_DIRECTION_SYMBOLS = {
  asc: <IconDropdownUp size={SORT_ICON_SIZE} />,
  desc: <IconDropdownDown size={SORT_ICON_SIZE} />,
};

const SortHeader = ({
  name,
  sortKey,
  currentSort,
  sortDirection,
}: {
  name: string;
  sortKey: string;
  currentSort: string;
  sortDirection: ProductCollectionSortDirection;
}) => {
  const isSelected = sortKey === currentSort;
  const location = useLocation();
  const queryParamDirection: keyof typeof SORT_DIRECTION_SYMBOLS =
    sortDirection === 'asc' ? 'desc' : 'asc';

  const queryParams = getSortedQuery({
    ...queryString.parse(location.search.replace(/^\?/, '')),
    offset: undefined,
    sort: sortKey,
    direction: isSelected ? queryParamDirection : undefined,
  });
  return (
    <S.ClickableHeader
      to={`${location.pathname}?${queryString.stringify(queryParams)}`}
    >
      <S.HeaderText>
        <S.ColumnName>{name}</S.ColumnName>
        <S.SortIconContainer show={isSelected}>
          {SORT_DIRECTION_SYMBOLS[isSelected ? sortDirection : 'desc']}
        </S.SortIconContainer>
      </S.HeaderText>
    </S.ClickableHeader>
  );
};

export type Column = {
  name: string;
  sortKey?: string;
};

type TableHeadersProps = {
  currentSort: string;
  sortDirection: ProductCollectionSortDirection;
  columns: Column[];
  isIndicatingNewColumn: boolean;
  onFinishedIndicatingNewColumn: () => void;
};
export const TableHeader = ({
  currentSort,
  sortDirection,
  columns,
  isIndicatingNewColumn,
  onFinishedIndicatingNewColumn,
}: TableHeadersProps) => {
  return (
    <thead>
      <S.HeaderTr>
        {columns.map((column, index) => {
          const isLastColumn = index === columns.length - 1;

          return (
            <TableHeaderColumn
              key={column.name}
              column={column}
              currentSort={currentSort}
              sortDirection={sortDirection}
              indicateNewColumn={isIndicatingNewColumn && isLastColumn}
              onFinishedIndicatingNewColumn={onFinishedIndicatingNewColumn}
            />
          );
        })}
      </S.HeaderTr>
    </thead>
  );
};

type TableHeaderColumnProps = {
  column: Column;
  currentSort: string;
  sortDirection: ProductCollectionSortDirection;
  indicateNewColumn: boolean;
  onFinishedIndicatingNewColumn: () => void;
};

const TableHeaderColumn = ({
  column,
  currentSort,
  sortDirection,
  indicateNewColumn,
  onFinishedIndicatingNewColumn,
}: TableHeaderColumnProps) => {
  const { name, sortKey } = column;
  const columnRef = useRef<HTMLTableHeaderCellElement>(null);

  // Try to approximate a good initial min-width for the column based on the length of the column
  // name, in order for it to fit on two lines.
  const spaceAroundName =
    S.TH_TOTAL_HORIZONTAL_PADDING +
    (sortKey ? Number(remCalc(SORT_ICON_SIZE)) : 0);
  const [style, setStyle] = useState<CSSProperties>({
    minWidth: `calc(${spaceAroundName}rem + ${name.length / 2}ch)`,
  });

  useEffect(() => {
    // Once rendered, set the current column width as the minimum width in order for the current
    // columns not to shrink when more columns are added.
    if (columnRef.current) {
      const rect = columnRef.current.getBoundingClientRect();
      setStyle({ minWidth: `${rect.width}px` });
    }
  }, []);

  return (
    <S.Th
      ref={columnRef}
      canBeSorted={!!sortKey}
      indicateNewColumn={indicateNewColumn}
      onAnimationEnd={onFinishedIndicatingNewColumn}
      style={style}
      data-test="ProductListColumnHeader"
    >
      {sortKey ? (
        <SortHeader {...{ sortKey, name, currentSort, sortDirection }} />
      ) : (
        <S.HeaderText>
          <S.ColumnName>{name}</S.ColumnName>
        </S.HeaderText>
      )}
    </S.Th>
  );
};
